// ColorEditor.jsx
import React, { useEffect, useState } from "react";
import { Box, Button, Typography, SwipeableDrawer } from "@mui/material";

const ColorPicker = ({ label, value, onChange }) => (
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ fontSize: "14px", mb: 0.5 }}>{label}:</Typography>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        p: 1,
      }}
    >
      <input
        type="text"
        value={value}
        placeholder="Select Color"
        onChange={() => null}
        style={{
          border: "none",
          outline: "none",
          flex: 1,
          fontSize: "14px",
        }}
      />
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "24px",
          height: "24px",
          padding: 0,
          border: "none",
        }}
      />
    </Box>
  </Box>
);

// Add this component inside ColorEditor.jsx
const PreviewButton = ({ colors }) => (
  <Box
    sx={{
      height: "40px",
      border: `1px solid ${colors.defaultBorder || "#e0e0e0"}`,
      borderRadius: "4px",
      backgroundColor: colors.defaultButton || "#ffffff",
      color: colors.defaultText || "#000000",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "all 0.2s",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: colors.hoverButton || "#f5f5f5",
        color: colors.hoverText || "#000000",
        border: `1px solid ${colors.hoverBorder || "#e0e0e0"}`,
      },
      "&:active": {
        backgroundColor: colors.activeButton || "#eeeeee",
        color: colors.activeText || "#000000",
        border: `1px solid ${colors.activeBorder || "#e0e0e0"}`,
      },
    }}
  >
    Example Button
  </Box>
);

const ColorEditor = ({ onSave, initialColors = null, edit, setEdit }) => {
  const [colors, setColors] = useState({
    defaultText: "",
    defaultButton: "",
    defaultBorder: "",
    hoverText: "",
    hoverButton: "",
    hoverBorder: "",
    activeText: "",
    activeButton: "",
    activeBorder: "",
  });

  useEffect(() => {
    if (initialColors) {
      setColors({ ...initialColors });
    } else {
      setColors({
        defaultText: "#000000",
        defaultButton: "#e3e3e336",
        defaultBorder: "#e0e0e0",
        hoverText: "#000000",
        hoverButton: "#d4d9de",
        hoverBorder: "#000",
        activeText: "#000000",
        activeButton: "#d4d9de",
        activeBorder: "#e0e0e0",
      });
    }
  }, [initialColors]);

  const handleSave = () => {
    onSave(colors);
  };

  return (
    <SwipeableDrawer
      anchor={"right"}
      open={edit.open}
      onClose={() =>
        setEdit({
          open: false,
          edit: "",
          title: "",
        })
      }
      onOpen={() => {}}
      PaperProps={{
        sx: {
          minHeight: "100vh",
          width: "300px",
          paddingBottom: "100px",
          overflowX: "hidden",
          overflowY: "scroll",
          /* Custom scrollbar styling */
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f4f4f4",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "10px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#555",
          },
        },
      }}
    >
      <Box
        sx={{
          bgcolor: "white",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          p: 2,
          width: "300px",
          height: "auto",
        }}
      >
        <Typography
          sx={{
            borderBottom: "1px solid #e0e0e0",
            pb: 1,
            mb: 2,
            fontSize: "14px",
          }}
        >
          Edit Colors
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ fontSize: "14px", mb: 1 }}>Preview</Typography>
          <PreviewButton colors={colors} />
        </Box>

        <ColorPicker
          label="Default Text Color"
          value={colors.defaultText}
          onChange={(value) => setColors({ ...colors, defaultText: value })}
        />
        <ColorPicker
          label="Default Button Color"
          value={colors.defaultButton}
          onChange={(value) => setColors({ ...colors, defaultButton: value })}
        />
        <ColorPicker
          label="Default Border Color"
          value={colors.defaultBorder}
          onChange={(value) => setColors({ ...colors, defaultBorder: value })}
        />
        <ColorPicker
          label="Hover Text Color"
          value={colors.hoverText}
          onChange={(value) => setColors({ ...colors, hoverText: value })}
        />
        <ColorPicker
          label="Hover Button Color"
          value={colors.hoverButton}
          onChange={(value) => setColors({ ...colors, hoverButton: value })}
        />
        <ColorPicker
          label="Hover Border Color"
          value={colors.hoverBorder}
          onChange={(value) => setColors({ ...colors, hoverBorder: value })}
        />
        <ColorPicker
          label="Click/Active Text Color"
          value={colors.activeText}
          onChange={(value) => setColors({ ...colors, activeText: value })}
        />
        <ColorPicker
          label="Click/Active Button Color"
          value={colors.activeButton}
          onChange={(value) => setColors({ ...colors, activeButton: value })}
        />
        <ColorPicker
          label="Click/Active Border Color"
          value={colors.activeBorder}
          onChange={(value) => setColors({ ...colors, activeBorder: value })}
        />

        <Button
          fullWidth
          variant="contained"
          onClick={handleSave}
          sx={{
            bgcolor: "#2e7d32",
            color: "white",
            "&:hover": {
              bgcolor: "#1b5e20",
            },
          }}
        >
          Save
        </Button>
      </Box>
    </SwipeableDrawer>
  );
};

export default ColorEditor;
